import i18n from '../i18n'
import { DEFAULT_LANGUAGE } from '../i18n/languages'

export function currentLocale() {
  return i18n.language || DEFAULT_LANGUAGE
}

// Preferências globais de formato de data/hora (setadas pelo AuthContext a partir do /me).
// null = automático (segue o locale). Aplicadas em formatDate/formatDateTime em todo o app.
let _dateFormat = null // 'DMY' | 'MDY' | 'YMD' | null
let _timeFormat = null // 'H24' | 'H12' | null

export function setDateTimePreferences({ dateFormat, timeFormat } = {}) {
  _dateFormat = dateFormat || null
  _timeFormat = timeFormat || null
}

export function getDateTimePreferences() {
  return { dateFormat: _dateFormat, timeFormat: _timeFormat }
}

// Backend é sempre UTC. ISO sem fuso (sem 'Z'/offset) é tratado como UTC; o navegador localiza.
const NAIVE_ISO_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/

function toDate(value) {
  if (value instanceof Date) return value
  if (typeof value === 'string' && NAIVE_ISO_RE.test(value.trim())) {
    return new Date(value.trim() + 'Z')
  }
  return new Date(value)
}

const pad = (n) => String(n).padStart(2, '0')

function fmtDateOnly(d, df) {
  const y = d.getFullYear()
  const m = pad(d.getMonth() + 1)
  const day = pad(d.getDate())
  switch (df) {
    case 'DMY': return `${day}/${m}/${y}`
    case 'MDY': return `${m}/${day}/${y}`
    case 'YMD': return `${y}-${m}-${day}`
    default: return d.toLocaleDateString(currentLocale(), { dateStyle: 'short' })
  }
}

function fmtTimeOnly(d, tf) {
  if (tf === 'H24') return `${pad(d.getHours())}:${pad(d.getMinutes())}`
  if (tf === 'H12') {
    let h = d.getHours()
    const ap = h < 12 ? 'AM' : 'PM'
    h = h % 12
    if (h === 0) h = 12
    return `${h}:${pad(d.getMinutes())} ${ap}`
  }
  return d.toLocaleTimeString(currentLocale(), { timeStyle: 'short' })
}

export function formatDate(value, options) {
  if (value === null || value === undefined || value === '') return ''
  const date = toDate(value)
  if (Number.isNaN(date.getTime())) return ''
  if (_dateFormat) return fmtDateOnly(date, _dateFormat)
  return date.toLocaleDateString(currentLocale(), options)
}

export function formatDateTime(value, options = { dateStyle: 'short', timeStyle: 'short' }) {
  if (value === null || value === undefined || value === '') return ''
  const date = toDate(value)
  if (Number.isNaN(date.getTime())) return ''
  if (_dateFormat || _timeFormat) {
    return `${fmtDateOnly(date, _dateFormat)} ${fmtTimeOnly(date, _timeFormat)}`
  }
  return date.toLocaleString(currentLocale(), options)
}

/** Só a hora (ex.: "15:10"), no fuso do navegador. Respeita a preferência H24/H12. */
export function formatTime(value) {
  if (value === null || value === undefined || value === '') return ''
  const date = toDate(value)
  if (Number.isNaN(date.getTime())) return ''
  return fmtTimeOnly(date, _timeFormat)
}

/** Data/hora com segundos (logs). Respeita as preferências DMY/MDY/YMD e H24/H12. */
export function formatDateTimeSeconds(value) {
  if (value === null || value === undefined || value === '') return ''
  const date = toDate(value)
  if (Number.isNaN(date.getTime())) return ''
  const secs = pad(date.getSeconds())
  if (_dateFormat || _timeFormat) {
    const time = fmtTimeOnly(date, _timeFormat).replace(/^(\d{1,2}:\d{2})/, `$1:${secs}`)
    return `${fmtDateOnly(date, _dateFormat)} ${time}`
  }
  return date.toLocaleString(currentLocale(), { dateStyle: 'short', timeStyle: 'medium' })
}

/** Preview com formatos explícitos (para a tela de Configurações, sem alterar o global). */
export function formatDateTimePreview(dateFormat, timeFormat, date = new Date()) {
  return `${fmtDateOnly(date, dateFormat || null)} ${fmtTimeOnly(date, timeFormat || null)}`
}

export function formatNumber(value, options) {
  if (value === null || value === undefined || value === '') return ''
  const n = typeof value === 'number' ? value : Number(value)
  if (Number.isNaN(n)) return ''
  return new Intl.NumberFormat(currentLocale(), options).format(n)
}
